import React, { Component, ReactElement } from "react";
import { GroundFloorDurableFunctionInstance } from "../models/DurableFunctionInstance";
import Table, { TableColumn, TableItem } from "./Table";
import _ from "lodash";
import TableCell from "./TableCell";
import DurableFunctionInstanceStatus from "./DurableFunctionInstanceStatus";
import TableBoxDataView, { TableBoxDataViewItem } from "./TableBoxDataView";
import { calcIsFailedAndAwaitingRestart, FRIENDLY_DATETIME_FORMAT } from "../utils/Utils";
import moment from "moment";


interface WorkloadInstancesBoxTableProps {
    startDate: string;
    endDate: string;
    instances: GroundFloorDurableFunctionInstance[];
}

interface WorkloadInstancesBoxTableState {
}

export default class WorkloadInstancesBoxTable extends Component<WorkloadInstancesBoxTableProps, WorkloadInstancesBoxTableState> {

    private static getSummary(instances: GroundFloorDurableFunctionInstance[]) {
        if (instances.length === 0) {
            return {
                total: 0,
                notCompleted: 0,
                percentCompleted: 0,
            }
        }

        const completed = instances.filter((instance, index, array) => instance.runtimeStatus === "Completed")

        return {
            total: instances.length,
            completed: completed.length,
            percentCompleted: ((completed.length) / instances.length) * 100
        };
    }

    private static sortSummary = (a: TableItem, b: TableItem): number => {

        const itemA = a as { school: string, instances: GroundFloorDurableFunctionInstance[] };
        const itemB = b as { school: string, instances: GroundFloorDurableFunctionInstance[] };

        const summaryA = WorkloadInstancesBoxTable.getSummary(itemA.instances);
        const summaryB = WorkloadInstancesBoxTable.getSummary(itemB.instances);

        return summaryB.percentCompleted - summaryA.percentCompleted;
    }

    private static runsSince(instances: GroundFloorDurableFunctionInstance[]) {
        return instances.findIndex(instance => instance.runtimeStatus === "Completed")  
    }

    private static sortRunsSince = (a: TableItem, b: TableItem): number => {

        const itemA = a as { school: string, instances: GroundFloorDurableFunctionInstance[] };
        const itemB = b as { school: string, instances: GroundFloorDurableFunctionInstance[] };

        const summaryA = WorkloadInstancesBoxTable.runsSince(itemA.instances);
        const summaryB = WorkloadInstancesBoxTable.runsSince(itemB.instances);

        //Order non completed above numbered
        if (summaryA === -1 || summaryB === -1) {
            return summaryA - summaryB;
        }

        return summaryB - summaryA;
    }

    private static columns: TableColumn[] = [
        { title: "School", propertyPath: "school", flex: 1 },
        { title: "Instances", propertyPath: "instances", flex: 10 },
        { title: "Summary", propertyPath: "summary", flex: 1, enableSort: true, sortFunc: WorkloadInstancesBoxTable.sortSummary },
        { title: "Runs since completed", propertyPath: "runsSince", flex: 1, enableSort: true, sortFunc: WorkloadInstancesBoxTable.sortRunsSince },
    ];

    constructor(props: WorkloadInstancesBoxTableProps) {
        super(props);

        this.state = {
        };
    }

    componentDidMount(): void {
    }

    async componentDidUpdate(prevProps: Readonly<WorkloadInstancesBoxTableProps>, prevState: Readonly<WorkloadInstancesBoxTableState>, snapshot?: any): Promise<void> {
    }

    private cellForColumn = (item: { school: string, instances: GroundFloorDurableFunctionInstance[] }, column: TableColumn, columnIndex: number): ReactElement<TableCell> | undefined => {

        if (column.propertyPath === "instances") {
            const boxData = item.instances.map(i => {

                const colourOverride = calcIsFailedAndAwaitingRestart(i) ? "red" : null;

                return {
                    date: i.createdAt,
                    title: (i.workloadName || i.playlistName || i.bulkWorkloadName) + ` at ${moment(i.createdAt).format(FRIENDLY_DATETIME_FORMAT)}`,
                    linkTo: `/cdb-admin/workloads/${i.durableFunctionInstanceId}`,
                    className: `table-box-data-view__record--${colourOverride || DurableFunctionInstanceStatus.calculateDefaultPillColour(i.runtimeStatus)}`,
                } as TableBoxDataViewItem;
            });

            return (
                <TableCell flex={column.flex}>
                    <TableBoxDataView startDate={this.props.startDate} endDate={this.props.endDate} data={boxData} />
                </TableCell>
            );
        }

        if (column.propertyPath === "summary") {
            const summary = WorkloadInstancesBoxTable.getSummary(item.instances);
            return (
                <TableCell flex={column.flex}>
                    <span>{summary.percentCompleted.toFixed(0)}%</span>
                    <span className="cdb-table__extra-detail">{summary.completed}/{summary.total}</span>
                </TableCell>
            );
        }

        if (column.propertyPath === "runsSince") {
            const runsSince = WorkloadInstancesBoxTable.runsSince(item.instances);
            return (
                <TableCell flex={column.flex}>                  
                    <span>{runsSince === -1 ? "Non completed" : runsSince}</span>                    
                </TableCell>
            );
        }

        return undefined;
    };
    
    render(): JSX.Element {

        const groupedWorkloads = _.groupBy(this.props.instances, wi => `${wi.tenantName}-${wi.schoolCode}`);
        const tableItems = _.keys(groupedWorkloads).sort().map((groupKey) => {
            return {
                school: groupKey,
                instances: groupedWorkloads[groupKey]
            };
        });

        return (
            <Table title={"Results"} items={tableItems} columns={WorkloadInstancesBoxTable.columns} cellForColumn={this.cellForColumn} enableSort={true} defaultSortColumnPath="summary" />
        );
    }
}